import { z } from 'zod'

import {
  BillingProvider,
  type CreateSalesLedEnterpriseRequest,
  CreateSalesLedEnterpriseRequestSkuEnum,
} from '@/api/generated'
import { ProductSeatType } from '@/types'

const seatError = 'Enter a whole number of seats, at least 1'
const seatCapError = 'Enter a whole number, at least 1, or leave empty'

export const salesOwnedEnterpriseSchema = z
  .object({
    name: z.string().trim().min(1, 'Enter an organization name'),
    domain: z.string().trim().min(1, 'Enter a domain'),
    email: z.string().trim().email('Enter a valid admin email'),
    seats: z.coerce.number<number>().int(seatError).min(1, seatError),
    seatCap: z
      .string()
      .trim()
      .refine((value) => value === '' || /^[1-9]\d*$/.test(value), seatCapError),
    defaultSeatType: z.enum([ProductSeatType.Dictation, ProductSeatType.Bundle]),
    createAdminUserIfMissing: z.boolean(),
    adminFirstName: z.string().trim(),
    adminLastName: z.string().trim(),
  })
  .superRefine((data, ctx) => {
    if (!data.createAdminUserIfMissing) return
    if (!data.adminFirstName) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['adminFirstName'],
        message: 'Enter the new admin’s first name',
      })
    }
    if (!data.adminLastName) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['adminLastName'],
        message: 'Enter the new admin’s last name',
      })
    }
  })

export type SalesOwnedEnterpriseFormValues = z.infer<typeof salesOwnedEnterpriseSchema>

export const salesOwnedDefaultValues: SalesOwnedEnterpriseFormValues = {
  name: '',
  domain: '',
  email: '',
  seats: 2,
  seatCap: '',
  defaultSeatType: ProductSeatType.Dictation,
  createAdminUserIfMissing: true,
  adminFirstName: '',
  adminLastName: '',
}

export const toSalesOwnedPayload = (data: SalesOwnedEnterpriseFormValues): CreateSalesLedEnterpriseRequest => ({
  billing_provider: BillingProvider.SalesOwned,
  sku: CreateSalesLedEnterpriseRequestSkuEnum.EnterpriseBundle,
  email: data.email,
  enterprise_name: data.name,
  domain: data.domain,
  seats: data.seats,
  ...(data.seatCap ? { seat_cap: Number(data.seatCap) } : {}),
  default_seat_type: data.defaultSeatType,
  create_admin_user_if_missing: data.createAdminUserIfMissing,
  ...(data.createAdminUserIfMissing
    ? { admin_first_name: data.adminFirstName, admin_last_name: data.adminLastName }
    : {}),
})
